<div class="d-flex flex-column gap-3" data-testid="prices-panel">
  <div class="d-flex flex-wrap align-items-start justify-content-between gap-3">
    <div class="d-flex flex-column gap-1">
      <h3 class="av-section-title m-0">{{ 'backoffice.plans.prices.title' | translate }}</h3>
      <p class="av-meta m-0">{{ 'backoffice.plans.prices.hint' | translate }}</p>
    </div>

    @if (canAddPrice()) {
      <button
        type="button"
        class="av-btn av-btn-primary"
        [disabled]="facade.isMutating()"
        (click)="openCreate()"
      >
        <mat-icon aria-hidden="true">add</mat-icon>
        {{ 'backoffice.plans.prices.add' | translate }}
      </button>
    }
  </div>

  @if (!facade.canWrite()) {
    <p class="av-meta m-0" data-testid="prices-restricted">
      {{ 'backoffice.plans.list.restricted_note' | translate }}
    </p>
  }

  <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="shared-product-notice">
    <mat-icon aria-hidden="true">info</mat-icon>
    <span>{{ 'backoffice.plans.prices.notice' | translate }}</span>
  </div>

  @switch (facade.pricesState()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.plans.prices.error.title' | translate"
        [message]="'backoffice.plans.prices.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      <div class="component-grid" data-testid="price-components">
        @for (component of facade.components(); track component.component_type) {
          <div
            class="component-card p-3 d-flex flex-column gap-2"
            [attr.data-component]="component.component_type"
          >
            <div class="d-flex align-items-center justify-content-between gap-2">
              <span class="cell-strong">{{ component.componentLabelKey | translate }}</span>

              @if (!component.is_permitted) {
                <av-status-chip
                  [label]="'backoffice.plans.prices.not_on_plan' | translate"
                  tone="neutral"
                ></av-status-chip>
              } @else if (component.isAbsent) {
                <av-status-chip
                  [label]="'backoffice.plans.prices.no_price_yet' | translate"
                  tone="warning"
                ></av-status-chip>
              } @else {
                <av-status-chip
                  [label]="
                    'backoffice.plans.prices.price_rows'
                      | translate: { count: component.price_count }
                  "
                  tone="positive"
                ></av-status-chip>
              }
            </div>

            <span class="av-meta">{{ component.sharedProductNameKey | translate }}</span>

            @if (!component.is_permitted && component.isAgent) {
              <p class="av-meta m-0" data-testid="agent-absent-note">
                {{ 'backoffice.plans.prices.agent_absent' | translate }}
              </p>
            } @else if (component.is_permitted && component.isAbsent && component.isAgent) {
              <p class="av-meta m-0" data-testid="agent-permitted-empty">
                {{ 'backoffice.plans.prices.agent_permitted_empty' | translate }}
              </p>
            }
          </div>
        }
      </div>

      @if (isEmpty()) {
        <av-empty-state
          icon="sell"
          [title]="'backoffice.plans.prices.empty.title' | translate"
          [message]="'backoffice.plans.prices.empty.message' | translate"
        ></av-empty-state>
      } @else {
        <div class="table-scroll">
          <table class="av-table w-100" data-testid="price-table">
            <thead>
              <tr>
                <th scope="col">
                  {{ 'backoffice.plans.prices.columns.component' | translate }}
                </th>
                <th scope="col">
                  {{ 'backoffice.plans.prices.columns.shared_product' | translate }}
                </th>
                <th scope="col">{{ 'backoffice.plans.prices.columns.offer' | translate }}</th>
                <th scope="col">{{ 'backoffice.plans.prices.columns.interval' | translate }}</th>
                <th scope="col" class="text-end">
                  {{ 'backoffice.plans.prices.columns.unit_amount' | translate }}
                </th>
                <th scope="col">
                  {{ 'backoffice.plans.prices.columns.stripe_price' | translate }}
                </th>
                <th scope="col">{{ 'backoffice.plans.prices.columns.status' | translate }}</th>
                <th scope="col" class="text-end">
                  {{ 'backoffice.plans.prices.columns.actions' | translate }}
                </th>
              </tr>
            </thead>

            <tbody>
              @for (price of facade.prices(); track price.id) {
                <tr>
                  <td class="cell-strong">{{ price.componentLabelKey | translate }}</td>

                  <td>
                    <div class="d-flex flex-column">
                      <span>{{ price.sharedProductNameKey | translate }}</span>
                      <span class="av-source">{{ price.stripeProductLabel }}</span>
                    </div>
                  </td>

                  <td class="cell-muted">{{ offerLabelKey(price.offer_type) | translate }}</td>

                  <td class="cell-muted">{{ price.billing_interval || unresolved }}</td>

                  <td class="text-end">
                    {{ price.unit_amount | number: '1.2-2' }} {{ price.currency || '' }}
                  </td>

                  <td class="av-source">
                    {{ price.stripePriceLabel }}
                    @if (!price.stripe_price_id) {
                      <mat-icon
                        class="warn-icon"
                        [matTooltip]="'backoffice.plans.prices.not_synced' | translate"
                        aria-hidden="true"
                      >
                        warning
                      </mat-icon>
                    }
                  </td>

                  <td>
                    <av-status-chip
                      [label]="
                        (price.is_active
                          ? 'backoffice.common.active'
                          : 'backoffice.common.inactive'
                        ) | translate
                      "
                      [tone]="price.is_active ? 'positive' : 'neutral'"
                    ></av-status-chip>
                  </td>

                  <td class="text-end">
                    @if (facade.canWrite()) {
                      <div class="d-flex justify-content-end gap-1">
                        <button
                          type="button"
                          class="av-btn"
                          [matTooltip]="'backoffice.common.actions.edit' | translate"
                          [disabled]="facade.isMutating()"
                          (click)="openEdit(price)"
                        >
                          <mat-icon aria-hidden="true">edit</mat-icon>
                        </button>

                        @if (price.is_active) {
                          <button
                            type="button"
                            class="av-btn av-btn-danger"
                            [matTooltip]="'backoffice.plans.prices.deactivate_confirm' | translate"
                            [disabled]="facade.isMutating()"
                            (click)="deactivate(price)"
                          >
                            <mat-icon aria-hidden="true">block</mat-icon>
                          </button>
                        }
                      </div>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.plans.prices.loading.title' | translate"
        [message]="'backoffice.plans.prices.loading.message' | translate"
      ></av-empty-state>
    }
  }
</div>
